
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';

interface Profile {
  id: string;
  phone_number: string | null;
  created_at: string | null;
  updated_at: string | null;
  subscription_status: string | null;
  timezone: string | null;
  is_verified: boolean | null;
  role: "user" | "admin" | null;
  admin_user_id: string | null;
}

interface UseProfileReturn {
  profile: Profile | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
  fetchProfile: (userId?: string) => Promise<Profile | null>;
  ensureProfileExists: (userId: string, phoneNumber: string | null) => Promise<Profile | null>;
  confirmProfileLink: (token: string) => Promise<boolean>;
}

export function useProfile(): UseProfileReturn {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const auth = useAuth();
  const userId = auth?.session?.user?.id;

  const fetchProfile = async (specificUserId?: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const targetUserId = specificUserId || userId;
      if (!targetUserId) {
        setProfile(null);
        return null;
      }

      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', targetUserId)
        .single();

      if (fetchError) {
        throw new Error(fetchError.message);
      }

      setProfile(data);
      return data;
    } catch (err: any) {
      console.error('Error fetching profile:', err);
      setError(err);
      setProfile(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const updateProfile = async (updates: Partial<Profile>) => {
    try {
      if (!userId) {
        throw new Error('No authenticated user');
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId);

      if (updateError) {
        throw new Error(updateError.message);
      }

      // Refetch to get the updated profile
      await fetchProfile();
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(err);
      throw err;
    }
  };

  const ensureProfileExists = async (userId: string, phoneNumber: string | null): Promise<Profile | null> => {
    try {
      // Check if profile exists
      let { data: existingProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        // PGRST116 means not found, any other error should be thrown
        throw fetchError;
      }

      // If profile doesn't exist, create it
      if (!existingProfile) {
        const { data: newProfile, error: insertError } = await supabase
          .from('profiles')
          .insert([{ 
            id: userId, 
            phone_number: phoneNumber, 
            is_verified: true 
          }])
          .select('*')
          .single();

        if (insertError) {
          throw insertError;
        }
        
        return newProfile;
      }
      
      return existingProfile;
    } catch (err: any) {
      console.error('Error ensuring profile exists:', err);
      throw err;
    }
  };

  const confirmProfileLink = async (token: string): Promise<boolean> => {
    try {
      // First, fetch the link request
      const { data: linkRequest, error: fetchError } = await supabase
        .from('admin_link_requests')
        .select('*')
        .eq('token', token)
        .single();
      
      if (fetchError) {
        throw new Error('Invalid or expired token');
      }
      
      if (linkRequest.is_verified) {
        return true; // Already verified
      }
      
      if (new Date(linkRequest.expires_at) < new Date()) {
        throw new Error('Link token has expired');
      }
      
      // Update the admin_link_requests table to mark as verified
      const { error: updateError } = await supabase
        .from('admin_link_requests')
        .update({
          is_verified: true,
          verified_at: new Date().toISOString()
        })
        .eq('id', linkRequest.id);
        
      if (updateError) {
        throw updateError;
      }
      
      // Update the profile to link to the admin
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ 
          admin_user_id: linkRequest.admin_id,
          role: 'admin'
        })
        .eq('id', linkRequest.profile_id);
        
      if (profileError) {
        throw profileError;
      }
      
      return true;
    } catch (err: any) {
      console.error('Error confirming profile link:', err);
      toast({
        title: "Verification Failed",
        description: err.message || "Could not verify the profile link",
        variant: "destructive"
      });
      return false;
    }
  };

  const refetch = async (): Promise<void> => {
    await fetchProfile();
  };

  return {
    profile,
    isLoading,
    error,
    refetch,
    updateProfile,
    fetchProfile,
    ensureProfileExists,
    confirmProfileLink
  };
}
